/**
 * JobDescriptionCard - Displays comprehensive job description for maintenance tasks
 * @migration-pattern-d
 *
 * FT-144 REWRITE: Store descriptions directly ON the job record
 * - Read from job.description instead of separate storage key
 * - Save to maintenance_jobs via storage.writeJSON (same pattern as completions)
 * - Zero separate storage key = guaranteed sync
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Save, X, BookOpen, AlertCircle, FileText } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import * as storage from '../../../../lib/storage';
import { getFFEGuidance } from '../../../../config/guidance/ffe.guidance';
import { getLSAGuidance } from '../../../../config/guidance/lsa.guidance';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';

const JobDescriptionCard = ({ job, jobDescription }) => {
  // FT-144 REWRITE: Get vessel context for storage key
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();

  // Use the job's description field if available, otherwise fall back to legacy jobDescription prop
  const desc = job?.description || job?.jobDescription || jobDescription || {};

  // Get unique job ID (prefer job.id, fall back to job.item for library jobs)
  const jobId = job?.id || job?.item || null;

  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedDesc, setEditedDesc] = useState({});
  const [showGuidance, setShowGuidance] = useState(false);

  // Find relevant guidance based on job
  const findGuidanceForJob = () => {
    if (!job) return null;

    // Try direct mapping based on equipment links
    if (job.equipmentLinks && job.equipmentLinks.length > 0) {
      const equipType = job.equipmentLinks[0];
      // Map equipment types to guidance keys
      const guidanceMap = {
        'extinguisher': 'ffe_extinguisher_control',
        'bucket': 'ffe_fire_pump', // Use pump guidance as template
        'hose': 'ffe_fire_hose',
        'pump': 'ffe_fire_pump',
        'detector': 'ffe_detector_fire',
        'fixedSystem': 'ffe_fixed_system_engine',
        'blanket': 'ffe_blanket'
      };

      const guidanceKey = guidanceMap[equipType];
      if (guidanceKey) {
        return getFFEGuidance(guidanceKey) || getLSAGuidance(guidanceKey);
      }
    }

    // Try to match based on job item name
    const jobName = getJobDisplayName(job).toLowerCase();
    if (jobName.includes('extinguisher')) return getFFEGuidance('ffe_extinguisher_control');
    if (jobName.includes('bucket')) return getFFEGuidance('ffe_fire_pump');
    if (jobName.includes('hose')) return getFFEGuidance('ffe_fire_hose');
    if (jobName.includes('pump')) return getFFEGuidance('ffe_fire_pump');
    if (jobName.includes('detector') || jobName.includes('detection')) return getFFEGuidance('ffe_detector_fire');
    if (jobName.includes('fixed') && jobName.includes('system')) return getFFEGuidance('ffe_fixed_system_engine');
    if (jobName.includes('blanket')) return getFFEGuidance('ffe_blanket');

    return null;
  };

  const guidance = findGuidanceForJob();

  // Phase 2.6: Job workflow functions temporarily disabled - will restore in Phase 3
  // GPT Pattern D - Core job workflow functions
  const createJob = async ({ title, equipmentId = null, dueAtISO = null, notes = '', priority = 'normal', assignees = [] }) => {
    console.warn('Phase 2.6: createJob disabled - will return in Phase 3');
    return null;
  };

  const assignJob = async (job, newAssignees) => {
    console.warn('Phase 2.6: assignJob disabled - will return in Phase 3');
    return null;
  };

  const startJob = async (job) => {
    console.warn('Phase 2.6: startJob disabled - will return in Phase 3');
    return null;
  };

  const blockJob = async (job, reason = '') => {
    console.warn('Phase 2.6: blockJob disabled - will return in Phase 3');
    return null;
  };

  const completeJob = async (job) => {
    console.warn('Phase 2.6: completeJob disabled - will return in Phase 3');
    return null;
  };

  const cancelJob = async (job, reason = '') => {
    console.warn('Phase 2.6: cancelJob disabled - will return in Phase 3');
    return null;
  };

  // Overdue / due-soon helpers (for badges)
  const isOverdue = (iso) => iso && new Date(iso).getTime() < Date.now();
  const isDueSoon = (iso, days = 7) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    const horizon = Date.now() + days * 24 * 3600 * 1000;
    return t >= Date.now() && t <= horizon;
  };

  // Phase 2.6: Service event logging temporarily disabled - will restore in Phase 3
  const logServiceEvent = async ({ equipmentId, serviceType = 'routine', doneAtISO = new Date().toISOString(), notes = '' }) => {
    console.warn('Phase 2.6: logServiceEvent disabled - will return in Phase 3');
    return null;
  };

  // FT-144 REWRITE: Load description from job.description on mount
  useEffect(() => {
    // Build default description from job data
    const defaultDesc = {};

    if (job?.defaultPurpose) {
      defaultDesc.purpose = job.defaultPurpose;
    }

    if (job?.defaultChecklist && job.defaultChecklist.length > 0) {
      // Convert checklist array to scope format with bullet points
      defaultDesc.scope = job.defaultChecklist.map(item => `• ${item}`);
    } else if (!desc.scope || (typeof desc.scope === 'string' && desc.scope === 'Perform required maintenance')) {
      // Fallback to generic text only if no better description exists
      defaultDesc.scope = 'Perform required maintenance';
    }

    // FT-144 REWRITE: Read directly from job.description (already loaded via job prop)
    if (job?.description) {
      // Job has custom description - merge with defaults for any missing fields
      setEditedDesc({ ...defaultDesc, ...job.description });
    } else {
      // FT-144 FIX: No custom description - defaults as base, desc overwrites
      // Previous bug: {...desc, ...defaultDesc} let defaults overwrite desc
      setEditedDesc({ ...defaultDesc, ...desc });
    }
  }, [job, desc]);

  // FT-144 REWRITE: Save description directly to job record in maintenance_jobs
  const handleSave = useCallback(() => {
    console.error('[FT-144j DEBUG 0] handleSave CALLED - jobId:', jobId, 'vesselId:', vessel?.id);

    if (!jobId) {
      console.error('[FT-144j DEBUG 0a] EXITING - no jobId');
      setIsEditing(false);
      return;
    }

    if (!vessel?.id) {
      console.error('[FT-144j DEBUG 0b] EXITING - no vesselId');
      setIsEditing(false);
      return;
    }

    // Build the description object
    const descriptionToSave = {
      ...editedDesc,
      lastModified: new Date().toISOString()
    };

    // Save directly to job record (same pattern as completions in useMaintenanceData)
    // FT-144i FIX: Do NOT wrap with toOrgScopedKey - storage.readJSON/writeJSON do it internally
    try {
      const jobsKey = `maintenance_jobs_${vessel.id}`;
      console.error('[FT-144j DEBUG 1] vesselId:', vessel.id, 'jobsKey:', jobsKey);

      const savedJobs = storage.readJSON(jobsKey) || [];
      console.error('[FT-144j DEBUG 2] savedJobs.length:', savedJobs.length);

      const updatedJobs = savedJobs.map(savedJob => {
        if (savedJob.id === jobId) {
          return {
            ...savedJob,
            description: descriptionToSave
          };
        }
        return savedJob;
      });

      // If job wasn't found, don't add it - description should only be saved to existing jobs
      const jobExists = savedJobs.some(j => j.id === jobId);
      console.error('[FT-144j DEBUG 3] jobExists:', jobExists, 'jobId:', jobId, 'savedJobIds:', savedJobs.map(j => j.id).slice(0, 5));

      if (!jobExists) {
        console.error('[FT-144j DEBUG 3b] EXITING EARLY - job not found');
        setIsEditing(false);
        return;
      }

      storage.writeJSON(jobsKey, updatedJobs); // Emits sr:storage:set event for sync
      console.error('[FT-144j DEBUG 4] WRITE COMPLETE - key:', jobsKey);

      console.log('[FT-144] Description saved to job record:', {
        jobId,
        vesselId: vessel.id,
        purpose: descriptionToSave.purpose?.substring(0, 50) + '...'
      });

      // Dispatch event so other components can react
      window.dispatchEvent(new CustomEvent('sr:jobs:updated', {
        detail: { vesselId: vessel.id, jobId }
      }));

      // FT-146 FIX: Immediately update local state so UI re-renders without waiting for parent
      // This ensures the saved description is displayed instantly
      setEditedDesc(descriptionToSave);

    } catch (err) {
      console.error('[FT-144] Failed to save description to job:', err.message);
    }

    setIsEditing(false);
  }, [jobId, editedDesc, vessel?.id]);

  // FT-144 REWRITE: Cancel editing - reload from job.description
  const handleCancel = useCallback(() => {
    // Build default description
    const defaultDesc = {};

    if (job?.defaultPurpose) {
      defaultDesc.purpose = job.defaultPurpose;
    }

    if (job?.defaultChecklist && job.defaultChecklist.length > 0) {
      defaultDesc.scope = job.defaultChecklist.map(item => `• ${item}`);
    }

    // Reload from job.description
    if (job?.description) {
      setEditedDesc({ ...defaultDesc, ...job.description });
    } else {
      setEditedDesc({ ...desc, ...defaultDesc });
    }

    setIsEditing(false);
  }, [job, desc]);

  // FT-146 FIX: Display description from local editedDesc state (which is authoritative after save)
  // editedDesc is updated both on mount (from job.description) and after save (from descriptionToSave)
  // This ensures immediate re-render without waiting for parent to pass updated job prop
  const displayDesc = editedDesc;

  // Check if this job covers open-flame protection requirement
  const coversOpenFlame = job?.meta?.coversRequirement === 'ffe_protection_open_flame' ||
                          job?.sourceRequirements?.includes('ffe_protection_open_flame');

  // FT-305 hardening: normalize notes/requirement for safe display
  // - Arrays (from RA-domain generators) coerced to newline-joined strings
  // - Non-string values fall through to null (safely skipped by the conditional)
  const requirementText = Array.isArray(job?.requirement) ? job.requirement.join('\n') : job?.requirement;
  const notesText = Array.isArray(job?.notes) ? job.notes.join('\n') : job?.notes;

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header uses flex-wrap and min-w-0 to ensure edit button is always visible on mobile */}
      <div className="bg-slate-700 px-4 sm:px-5 py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h2 className="text-sm sm:text-base font-semibold text-white whitespace-nowrap">Job Description</h2>
          {coversOpenFlame && (
            <span className="hidden sm:flex px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full items-center gap-1">
              🔥 Covers: Open Flame
            </span>
          )}
        </div>
        {/* FT-186: Inline edit removed - editing now done via Edit Job button in header */}
      </div>

      <div className="px-6 py-5">
      <div className="prose max-w-none text-gray-600">
        {/* Purpose statement if available */}
        {isEditing ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
            <input
              type="text"
              value={editedDesc.purpose || ''}
              onChange={(e) => setEditedDesc({ ...editedDesc, purpose: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter the purpose of this maintenance task..."
            />
          </div>
        ) : (
          displayDesc.purpose && (
            <p className="text-sm sm:text-base mb-4 leading-relaxed font-medium text-gray-700">
              {displayDesc.purpose}
            </p>
          )
        )}

        {/* Scope of Work - now handles both string and array formats */}
        {isEditing ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work</label>
            <textarea
              value={typeof editedDesc.scope === 'string' ? editedDesc.scope : (editedDesc.scope || []).join('\n')}
              onChange={(e) => setEditedDesc({ ...editedDesc, scope: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="8"
              placeholder="Enter scope of work, one item per line. Use • for bullet points..."
            />
            <p className="text-xs text-gray-500 mt-1">Tip: Start lines with • for bullet points</p>
          </div>
        ) : (
          displayDesc.scope && (
            <div className="mb-4">
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">Scope of Work</h3>
              {Array.isArray(displayDesc.scope) ? (
              <div className="space-y-1">
                {displayDesc.scope.map((item, index) => {
                  // Handle section headers (items without bullet points)
                  if (!item.startsWith('•') && item !== '') {
                    return (
                      <div key={index} className={`font-medium text-gray-700 ${index > 0 ? 'mt-3' : ''}`}>
                        {item}
                      </div>
                    );
                  }
                  // Handle bullet points
                  if (item.startsWith('•')) {
                    return (
                      <div key={index} className="text-xs sm:text-sm pl-4">
                        {item}
                      </div>
                    );
                  }
                  // Handle empty lines for spacing
                  return <div key={index} className="h-2" />;
                })}
              </div>
            ) : (
              // Handle string format - split by newlines and display
              <div className="space-y-1">
                {displayDesc.scope.split('\n').map((line, index) => (
                  <div key={index} className="text-xs sm:text-sm">
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
        }

        {/* Completion Requirements - Editable */}
        {isEditing ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Completion Requirements</label>
            <input
              type="text"
              value={editedDesc.completion || ''}
              onChange={(e) => setEditedDesc({ ...editedDesc, completion: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Record check date and any defects found"
            />
          </div>
        ) : (
          displayDesc.completion && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">Completion</h3>
              <p className="text-xs sm:text-sm">{displayDesc.completion}</p>
            </div>
          )
        )}

        {/* FT-305: Requirement - display-only (read from job.requirement top-level, NOT displayDesc) */}
        {typeof requirementText === 'string' && requirementText.trim() && (
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 text-gray-600" />
              Requirement
            </h3>
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed break-words">{requirementText}</p>
          </div>
        )}

        {/* FT-305: Notes - display-only (read from job.notes top-level, whitespace-pre-wrap for newlines) */}
        {typeof notesText === 'string' && notesText.trim() && (
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-gray-600" />
              Notes
            </h3>
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">{notesText}</p>
          </div>
        )}

        {/* Tools Required - Display only (not editable for now) */}
        {displayDesc.tools && displayDesc.tools.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">Tools & Equipment</h3>
            <ul className="list-disc list-inside space-y-1">
              {displayDesc.tools.map((tool, index) => (
                <li key={index} className="text-xs sm:text-sm">{tool}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Safety Considerations - Display only (not editable for now) */}
        {displayDesc.safety && displayDesc.safety.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">Safety Considerations</h3>
            <ul className="list-disc list-inside space-y-1">
              {displayDesc.safety.map((item, index) => (
                <li key={index} className="text-xs sm:text-sm text-orange-700">{item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Guidance Checklist - From FFE guidance */}
        {guidance && guidance.checklist && (
          <div className="mb-4">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm sm:text-base font-medium text-purple-900 flex items-center">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Equipment Guidance Checklist
                </h3>
                <button
                  onClick={() => setShowGuidance(!showGuidance)}
                  className="text-xs text-purple-600 hover:text-purple-700 underline"
                >
                  {showGuidance ? 'Hide' : 'Show'} Details
                </button>
              </div>

              {/* Determine frequency based on job interval */}
              {(() => {
                const frequency = job?.unit === 'months' && job?.interval === 1 ? 'monthly' :
                                job?.unit === 'months' && job?.interval === 3 ? 'quarterly' :
                                job?.unit === 'years' ? 'annual' : 'monthly';
                const checklist = guidance.checklist[frequency];

                if (!checklist) return null;

                return (
                  <div>
                    <p className="text-xs text-purple-700 mb-2">
                      {frequency.charAt(0).toUpperCase() + frequency.slice(1)} inspection points from equipment guidance:
                    </p>
                    <div className="space-y-2">
                      {(showGuidance ? checklist : checklist.slice(0, 3)).map((item, idx) => (
                        <label key={idx} className="flex items-start">
                          <input
                            type="checkbox"
                            className="mt-0.5 mr-2 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
                          />
                          <span className="text-xs sm:text-sm text-gray-700">{item}</span>
                        </label>
                      ))}
                      {!showGuidance && checklist.length > 3 && (
                        <p className="text-xs text-purple-600 italic">
                          +{checklist.length - 3} more items...
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Original checklist if no comprehensive jobDescription */}
        {!displayDesc.purpose && displayDesc.checklist && displayDesc.checklist.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">Inspection Checklist</h3>
            <ul className="list-disc list-inside space-y-1">
              {displayDesc.checklist.map((item, index) => (
                <li key={index} className="text-xs sm:text-sm">{item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Certificates and Warnings */}
        {(job.certificate || job.warning) && (
          <div className="mt-4 space-y-3">
            {job.certificate && (
              <div className="flex items-start p-2 sm:p-3 bg-blue-50 rounded-lg">
                <span className="text-xs sm:text-sm font-medium text-blue-900 mr-1">Certificate Required: </span>
                <span className="text-xs sm:text-sm text-blue-700">{job.certificate}</span>
              </div>
            )}
            {job.warning && (
              <div className="flex items-start p-2 sm:p-3 bg-orange-50 rounded-lg">
                <p className="text-xs sm:text-sm text-orange-900">{job.warning}</p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default JobDescriptionCard;
